import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Square, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VoiceRecorder Component
 * Allows users to record voice messages for AI consultation
 * Mobile-optimized with visual feedback
 */
const VoiceRecorder = ({ 
  onTranscription, 
  onRecordingStart,
  onRecordingEnd,
  language = 'hi',
  disabled = false,
  className = ''
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Audio level visualization
  const visualizeAudio = () => {
    if (!analyserRef.current || !isRecording) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average level
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(Math.min(100, average * 1.5));
    
    animationRef.current = requestAnimationFrame(visualizeAudio);
  };

  // Start recording
  const startRecording = async () => {
    setError(null);
    audioChunksRef.current = [];
    
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      // Setup audio analyser for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Create media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Process recorded audio
        await processRecording();
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms
      
      setIsRecording(true);
      setRecordingTime(0);
      onRecordingStart?.();
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start visualization
      visualizeAudio();
      
    } catch (err) {
      console.error('Recording error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied. Please allow microphone access.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setIsRecording(false);
    setAudioLevel(0);
    onRecordingEnd?.();
  };

  // Process and transcribe recording
  const processRecording = async () => {
    if (audioChunksRef.current.length === 0) return;
    
    setIsProcessing(true);
    
    try {
      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, { 
        type: mediaRecorderRef.current?.mimeType || 'audio/webm' 
      });
      
      // Create form data
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);
      
      // Send to backend for transcription
      const response = await fetch(`${API_URL}/api/sankat-mochan/voice/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text && data.text.trim()) {
          onTranscription?.(data.text);
        } else {
          setError('Could not understand the audio. Please try again.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Transcription failed. Please try again.');
      }
    } catch (err) {
      console.error('Processing error:', err);
      setError('Failed to process recording. Please try again.');
    }
    
    setIsProcessing(false);
  };

  // Cancel recording
  const cancelRecording = () => {
    audioChunksRef.current = [];
    stopRecording();
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Recording button click handler
  const handleClick = () => {
    if (disabled || isProcessing) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Error message */}
      {error && (
        <div className="absolute bottom-full mb-2 left-0 right-0 bg-red-500/90 text-white text-xs px-3 py-2 rounded-lg">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 hover:text-red-200"
          >
            <X className="w-3 h-3 inline" />
          </button>
        </div>
      )}
      
      {/* Recording UI */}
      {isRecording ? (
        <div className="flex items-center gap-2 bg-red-500/20 px-3 py-2 rounded-xl border border-red-500/30">
          {/* Audio level indicator */}
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-red-500 rounded-full transition-all duration-75"
                style={{ 
                  height: `${Math.max(4, (audioLevel / 100) * 20 * (1 - Math.abs(2 - i) * 0.2))}px` 
                }}
              />
            ))}
          </div>
          
          {/* Recording time */}
          <span className="text-red-400 text-sm font-mono min-w-[40px]">
            {formatTime(recordingTime)}
          </span>
          
          {/* Stop button */}
          <button
            onClick={stopRecording}
            className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
            title="Stop recording"
            data-testid="stop-recording-btn"
          >
            <Square className="w-4 h-4" fill="white" />
          </button>
          
          {/* Cancel button */}
          <button
            onClick={cancelRecording}
            className="p-1.5 text-red-400 hover:text-red-300 transition-colors"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={handleClick}
          disabled={disabled || isProcessing}
          className={`p-3 rounded-xl transition-all ${
            isProcessing 
              ? 'bg-amber-500/20 text-amber-400' 
              : 'bg-gradient-to-r from-red-500/20 to-pink-500/20 text-red-400 hover:from-red-500/30 hover:to-pink-500/30 active:scale-95'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={isProcessing ? 'Processing...' : 'Record voice message'}
          data-testid="voice-record-btn"
        >
          {isProcessing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </button>
      )}
    </div>
  );
};

export default VoiceRecorder;
